import { useCallback, useEffect, useRef, useState } from 'react'

import type { ChartMode } from '../../charts/cashflow-utils'
import type { CashFlowSelection } from '../../charts/idv-grouping'

export type DrillLevel =
  | { level: 0 }
  | { level: 1 }
  | { level: 2; selection: CashFlowSelection }
  | { level: 3; parentPiid: string }

export function useDrillDown(
  recipientId: string,
  chartMode: ChartMode,
  onChartModeChange: (mode: ChartMode) => void,
  canAccessPremium: boolean,
) {
  const [selection, setSelection] = useState<CashFlowSelection>(null)
  const [drillLevel, setDrillLevel] = useState<DrillLevel>({ level: 0 })
  const [showGateOverlay, setShowGateOverlay] = useState(false)

  // Context-aware chart mode: each context (all awards vs specific award)
  // remembers the user's explicit toggle independently.
  // Sync from parent when compact toggle changes chartMode externally.
  const [allAwardsMode, setAllAwardsMode] = useState<ChartMode>(chartMode)
  const prevChartModeRef = useRef(chartMode)
  if (prevChartModeRef.current !== chartMode) {
    prevChartModeRef.current = chartMode
    setAllAwardsMode(chartMode)
  }
  const [specificAwardMode, setSpecificAwardMode] = useState<ChartMode | null>(
    null,
  )

  // Reset drill-down state when navigating between recipients (Pages Router
  // reuses the component tree for dynamic routes, so local state persists)
  const prevRecipientIdRef = useRef(recipientId)
  if (prevRecipientIdRef.current !== recipientId) {
    prevRecipientIdRef.current = recipientId
    setSelection(null)
    setDrillLevel({ level: 0 })
    setSpecificAwardMode(null)
  }

  const isSpecificAward = selection !== null
  const effectiveMode: ChartMode = isSpecificAward
    ? (specificAwardMode ?? 'cumulative')
    : allAwardsMode

  // Keep parent in sync with all-awards mode (so compact view is correct on collapse)
  useEffect(() => {
    onChartModeChange(allAwardsMode)
  }, [allAwardsMode, onChartModeChange])

  const handleModeToggle = useCallback(
    (mode: ChartMode) => {
      if (isSpecificAward) {
        setSpecificAwardMode(mode)
      } else {
        setAllAwardsMode(mode)
      }
    },
    [isSpecificAward],
  )

  const handleSelect = useCallback(
    (sel: CashFlowSelection): boolean => {
      if (!canAccessPremium && sel !== null) {
        setShowGateOverlay(true)
        return false
      }
      setSelection(sel)
      if (sel === null) {
        setDrillLevel({ level: 0 })
      } else {
        setDrillLevel({ level: 2, selection: sel })
      }
      return true
    },
    [canAccessPremium],
  )

  const handleDrillNavigate = useCallback((level: DrillLevel) => {
    setDrillLevel(level)
    if (level.level === 0) {
      setSelection(null)
    } else if (level.level === 2 && 'selection' in level) {
      setSelection(level.selection)
    }
  }, [])

  const handleRequestGate = useCallback(() => {
    setShowGateOverlay(true)
  }, [])

  const handleDismissGate = useCallback(() => {
    setShowGateOverlay(false)
  }, [])

  const handleCategoryDrill = useCallback(() => {
    if (!canAccessPremium) {
      setShowGateOverlay(true)
      return
    }
    setDrillLevel({ level: 1 })
  }, [canAccessPremium])

  return {
    selection,
    setSelection,
    drillLevel,
    setDrillLevel,
    effectiveMode,
    isSpecificAward,
    showGateOverlay,
    handleModeToggle,
    handleSelect,
    handleDrillNavigate,
    handleRequestGate,
    handleDismissGate,
    handleCategoryDrill,
  }
}
